import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';

export const DuplicateForecastSystemModal = ({
    isOpen,
    onClose,
    forecastName,
    onAccept
}) => {
    const [name, setName] = useState(forecastName || '');
    const [copyAllScenarios, setCopyAllScenarios] = useState(false);
    const [onlyActiveScenario, setOnlyActiveScenario] = useState(true);
    const [copyProductMix, setCopyProductMix] = useState(true);
    const [copyMonthlyAllocation, setCopyMonthlyAllocation] = useState(true);
    const [copyChannelMix, setCopyChannelMix] = useState(true);

    useEffect(() => {
        if (!isOpen) return;
        if (forecastName) {
            setName(forecastName);
        }
    }, [isOpen, forecastName]);

    const handleAcceptActions = async () => {
        if (!name) {
            toast.error(`Empty name.`, { position: "bottom-right" });
            return;
        }
        await onAccept({
            name: name,
            options: {
                copyAllScenarios,
                onlyActiveScenario,
                copyProductMix,
                copyMonthlyAllocation,
                copyChannelMix,
            }
        });
    }

    const handleClose = useCallback(() => {
        onClose();
        setName('');
    }, [onClose]);

    // Handle Esc key press
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, handleClose]);

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                    aria-hidden="true"
                    onClick={handleClose}
                ></div>
                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
                <div 
                    className="inline-block transform rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg font-bold mb-2">Duplicate plan</h3>
                        <div className="flex flex-col gap-1 mb-2">
                            <span className="font-medium">Plan name:</span>
                            <div className="flex-1">
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value)
                                    }}
                                    className="w-full p-2.5 shadow-sm border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                            </div>
                         </div>
                        <div className="flex flex-col gap-2 mb-2">
                            <span className="font-medium">What to copy:</span>
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={copyAllScenarios}
                                        onChange={(e) => {
                                            const v = e.target.checked;
                                            setCopyAllScenarios(v);
                                            setOnlyActiveScenario(!v);
                                        }}
                                        className="accent-black"
                                    />
                                    <span>Copy all scenarios (Base/Growth/Aggressive)</span>
                                </label>
                                {!copyAllScenarios && (
                                    <label className="flex items-center gap-2 text-sm pl-5">
                                        <input
                                            type="radio"
                                            name="scenarioScope"
                                            checked={onlyActiveScenario}
                                            onChange={() => setOnlyActiveScenario(true)}
                                            className="accent-black"
                                        />
                                        <span>Only copy active scenario (base)</span>
                                    </label>
                                )}
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={copyProductMix}
                                        onChange={(e) => setCopyProductMix(e.target.checked)}
                                        className="accent-black"
                                    />
                                    <span>Copy product mix & AOV</span>
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={copyMonthlyAllocation}
                                        onChange={(e) => setCopyMonthlyAllocation(e.target.checked)}
                                        className="accent-black"
                                    />
                                    <span>Copy monthly allocation</span>
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={copyChannelMix}
                                        onChange={(e) => setCopyChannelMix(e.target.checked)}
                                        className="accent-black"
                                    />
                                    <span>Copy channel mix</span>
                                </label>
                            </div>
                        </div>
                         <div className="py-3 sm:flex sm:flex-row-reverse">
                            <button
                                type="button"
                                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                onClick={handleAcceptActions}
                            >
                                Accept
                            </button>
                            <button
                                type="button"
                                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                         </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};